'use client';

import { FaStar } from 'react-icons/fa';

import type {
  ProductDetailData,
  ProductReview,
} from '@eliadmin/core/src/api/products/types/product-types';

import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

import { Button } from '../../ui/button';
import { useQuery } from '@tanstack/react-query';
import { fetchProductReviews } from '@eliadmin/core/src/api/products/product-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { FaPencil } from 'react-icons/fa6';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/nextjs';

export function ProductReviews({
  product,
  locale,
}: {
  product: ProductDetailData;
  locale: string;
}) {
  const { t } = useTranslations();
  const openDialog = useDialogStore(s => s.open);
  const sectionRef = useRef<HTMLElement>(null);
  const reviewImportPending = useRef(false);
  const [visibleProductSlug, setVisibleProductSlug] = useState<string>();
  const hasEnteredViewport = visibleProductSlug === product.slug;

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || hasEnteredViewport) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleProductSlug(product.slug);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;

        setVisibleProductSlug(product.slug);
        observer.disconnect();
      },
      { rootMargin: '250px 0px' },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [hasEnteredViewport, product.slug]);

  const {
    data: reviews,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, product.slug],
    queryFn: () => fetchProductReviews({ locale, slug: product.slug }),
    enabled: hasEnteredViewport,
  });

  const reviewsPending = !hasEnteredViewport || isLoading;
  const reviewCount = reviews?.data.length ?? product.ratingCount ?? product.reviews_count;

  const handleCreateReviewClick = async () => {
    if (reviewImportPending.current) return;
    reviewImportPending.current = true;
    try {
      const { AddReviewDialog } =
        await import('@eliadmin/core/src/components/product/dialog/add-review-dialog');
      openDialog({ id: AddReviewDialog.id, content: <AddReviewDialog slug={product.slug} /> });
    } catch (error) {
      captureException(error);
    } finally {
      reviewImportPending.current = false;
    }
  };

  // An unrated product has no average; never manufacture a five-star fallback.
  const averageRating =
    reviewCount && reviewCount > 0
      ? reviews?.data.length
        ? reviews.data.reduce((sum, review) => sum + Number(review.rating), 0) / reviews.data.length
        : Number(product.rating)
      : 0;
  const hasRating = Number.isFinite(averageRating) && averageRating >= 1 && averageRating <= 5;

  const getReviewCountText = (count: number) => {
    if (count === 1) return `${count} ${t('text3511')}`;
    if (count >= 2 && count <= 4) return `${count} ${t('text3512')}`;
    return `${count} ${t('text3513')}`;
  };

  return (
    <section ref={sectionRef} className={cn('custom-section', 'gap-6 py-8 sm:py-10 lg:py-12')}>
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col items-start justify-start sm:flex-row sm:items-center sm:justify-between',
        )}
      >
        <h2 className="text-lg font-semibold text-foreground sm:text-xl lg:text-2xl">
          {t('text447')}
        </h2>
        <Button onClick={handleCreateReviewClick} className="w-full gap-3 sm:w-fit">
          <FaPencil size={20} /> {t('text585')}
        </Button>
      </div>
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col items-start justify-start gap-12 md:flex-row md:justify-between md:gap-6 lg:gap-12',
        )}
      >
        <div className="flex w-full items-start justify-start gap-6 md:max-w-[604px]">
          <div className="flex flex-col items-start justify-start gap-2">
            <p className="text-6xl font-semibold text-black sm:text-7xl md:text-8xl lg:text-[140px] xl:text-[160px]">
              {hasRating ? averageRating.toFixed(1) : '—'}
              {hasRating && (
                <span className="text-lg font-normal text-[#666464] sm:text-xl lg:text-2xl">
                  /5
                </span>
              )}
            </p>
            {reviewCount != null && (
              <p className="text-lg font-normal text-[#666464] sm:text-xl lg:text-2xl">
                ({getReviewCountText(reviewCount)})
              </p>
            )}
          </div>
          <div className="flex w-full max-w-[317px] flex-col items-start justify-start gap-2">
            {[5, 4, 3, 2, 1].map(starRating => {
              const ratingCount =
                reviews?.data.filter(review => Number(review.rating) === starRating).length || 0;
              const barWidth = reviews?.data.length ? (ratingCount / reviews.data.length) * 100 : 0;

              return (
                <div key={starRating} className="flex w-full items-center justify-start gap-1">
                  <FaStar size={18} className="min-h-[18px] min-w-[18px] text-[#EDB000]" />
                  <p className=" whitespace-nowrap text-lg text-foreground sm:text-xl lg:text-2xl">
                    {starRating}
                  </p>
                  <div className="mx-1 flex h-[6px] w-full min-w-2 items-center justify-start rounded-full bg-[#EBEBEB]">
                    <div
                      className="h-[6px] rounded-full bg-foreground"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {reviewsPending && <Spinner className={cn('', 'ea-product-detail-tab-reviews__spinner')} />}
        {reviews?.data.length ? (
          <div className="flex w-full max-w-[604px] flex-col items-start justify-start gap-4">
            {reviews.data.map(review => (
              <ProductReviewCard key={review.slug} review={review} />
            ))}
          </div>
        ) : (
          !reviewsPending &&
          !isError &&
          reviews?.data.length === 0 && (
            <div
              className={cn(
                'mt-4 w-full text-center text-lg font-semibold text-muted-foreground',
                'ea-product-detail-tab-reviews__no-reviews-text',
              )}
            >
              {t('text608')}
            </div>
          )
        )}
      </div>
    </section>
  );
}

function ProductReviewCard({ review }: { review: ProductReview }) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${date.getFullYear()}`;
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[8px] border border-border p-4 sm:p-5 lg:p-6">
      <div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row sm:justify-between">
        <div className="flex flex-col items-start justify-start gap-2">
          <p className="text-sm font-medium text-foreground">
            {review.name} {review.surname}
          </p>
          <ProductRating
            rating={review.rating}
            className={{
              main: 'flex items-center justify-start gap-2',
              starIcon: 'text-[#EDB000]',
              starDisabled: 'text-[#EBEBEB]',
            }}
          />
        </div>
        <span className="text-sm font-normal text-[#666464]">{formatDate(review.created_at)}</span>
      </div>
      <HtmlDescription
        content={review.review}
        color="muted-foreground"
        size="sm"
        className={cn('w-full text-left', 'ea-product-reviews__description')}
      />
    </div>
  );
}
